import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";

import { cn } from "@/lib/utils";

export interface DataTableColumn<T> {
  key: string;
  header: ReactNode;
  render: (row: T) => ReactNode;
  /** Classe aplicada à célula (td). */
  className?: string;
  /** Classe aplicada ao cabeçalho (th). */
  headerClassName?: string;
}

interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  rows: T[];
  getRowKey: (row: T) => string;
  /** Se informado, a linha vira clicável e navega para a rota. */
  getRowHref?: (row: T) => string;
  className?: string;
}

/**
 * Tabela genérica e leve (sem dependência de tanstack-table). Recebe a
 * definição de colunas + linhas e, opcionalmente, torna cada linha
 * clicável. Reutilizável em qualquer listagem (KBs, times, agentes...).
 */
export function DataTable<T>({
  columns,
  rows,
  getRowKey,
  getRowHref,
  className,
}: DataTableProps<T>) {
  const navigate = useNavigate();
  return (
    <div
      className={cn(
        "shadow-card overflow-x-auto rounded-2xl border border-line bg-surface",
        className,
      )}
    >
      <table className="w-full text-sm">
        <thead className="bg-canvas text-[10px] uppercase tracking-wide text-ink-500">
          <tr>
            {columns.map((c) => (
              <th
                key={c.key}
                className={cn(
                  "px-4 py-2.5 text-left font-semibold",
                  c.headerClassName,
                )}
              >
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-line text-ink-900">
          {rows.map((row) => {
            const href = getRowHref?.(row);
            return (
              <tr
                key={getRowKey(row)}
                className={cn(
                  href && "cursor-pointer hover:bg-canvas/60",
                )}
                onClick={href ? () => navigate(href) : undefined}
                onKeyDown={
                  href
                    ? (e) => {
                        if (e.key === "Enter") navigate(href);
                      }
                    : undefined
                }
                tabIndex={href ? 0 : undefined}
                role={href ? "link" : undefined}
              >
                {columns.map((c) => (
                  <td key={c.key} className={cn("px-4 py-2", c.className)}>
                    {c.render(row)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
